import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCalendarAlt,
  faClock,
  faStar,
  faArrowRight,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import { db } from "../../../services/firebase";
import { collection, getDocs, query, limit, where } from "firebase/firestore";
import "../../../styles/pages/home.css";

// Uvozimo slike
import heroImage from "../../../images/narucivanje-hero.jpg";

// Definirajmo interface za Service objekt
interface Service {
  id: string;
  name: string;
  price: number;
  salePrice?: number;
  isOnSale: boolean;
  discountPercentage?: number;
  duration?: number;
  description?: string;
  category?: string;
}

// Pomoćna funkcija za dohvat nasumičnih usluga
const getRandomItems = <T,>(array: T[], count: number): T[] => {
  const shuffled = [...array].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
};

const Home: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [isIntersecting, setIsIntersecting] = useState<{
    [key: string]: boolean;
  }>({
    about: false,
    services: false,
    cta: false,
  });

  // Efekt za animaciju sekcija prilikom scrollanja
  useEffect(() => {
    const sections = ["about", "services", "cta"];

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id;
          if (sections.includes(id)) {
            setIsIntersecting((prev) => ({
              ...prev,
              [id]: entry.isIntersecting,
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    sections.forEach((id) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => {
      sections.forEach((id) => {
        const element = document.getElementById(id);
        if (element) observer.unobserve(element);
      });
    };
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const servicesCollection = collection(db, "services");
        // Dohvati maksimalno 6 usluga za bolju performansu
        const servicesQuery = query(servicesCollection, limit(6));
        const servicesSnapshot = await getDocs(servicesQuery);
        const servicesList = servicesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Service[];

        // Podijeli usluge na one koje su na sniženju i one koje nisu
        const discountedServices = servicesList.filter(
          (service) => service.isOnSale
        );
        const regularServices = servicesList.filter(
          (service) => !service.isOnSale
        );

        // Nasumično odaberi usluge koje nisu na sniženju
        // Ako nema dovoljno usluga na popustu, prikazat će više regularnih
        const discountedToShow = Math.min(discountedServices.length, 2);
        const regularToShow = 4 - discountedToShow;

        const randomRegularServices = getRandomItems(
          regularServices,
          regularToShow
        );

        // Kombiniraj snižene i nasumične redovne usluge
        setServices([
          ...discountedServices.slice(0, discountedToShow),
          ...randomRegularServices,
        ]);
        setLoading(false);
      } catch (error) {
        console.error("Greška pri dohvaćanju usluga: ", error);
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  return (
    <div className="home-page">
      {/* Hero sekcija - s poboljšanim animacijama i layoutom */}
      <section className="hero-section">
        <div className="hero-shapes">
          <div className="hero-shape hero-shape-1"></div>
          <div className="hero-shape hero-shape-2"></div>
          <div className="hero-shape hero-shape-3"></div>
          <div className="hero-shape hero-shape-4"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title animate-fade-in">
              <span className="hero-title-sub">Beauty Concept</span>
              <span className="hero-title-main">Veronika</span>
            </h1>
            <p className="hero-description animate-fade-in-delay">
              Specijalizirani smo za tretmane koji ističu prirodnu ljepotu vaših
              očiju. Prepustite se stručnim rukama i uživajte u vrhunskoj njezi
              trepavica i obrva.
            </p>
            <div className="hero-cta animate-fade-in-delay-2">
              <Link to="/booking" className="primary-button">
                <FontAwesomeIcon icon={faCalendarAlt} className="button-icon" />
                Rezerviraj termin
              </Link>
              <Link to="/services" className="secondary-button">
                Istražite tretmane
              </Link>
            </div>
          </div>
          <div className="hero-image animate-slide-in-right">
            <img
              src={heroImage}
              alt="Beauty Concept Veronika tretmani"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* O nama sekcija - s animiranim pojavljivanjem */}
      <section
        id="about"
        className={`about-section ${
          isIntersecting.about ? "section-animate" : ""
        }`}
      >
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">O nama</span>
            <h2 className="section-title">Upoznajte Beauty Concept Veronika</h2>
          </div>
          <div className="about-content">
            <div className="about-features">
              <div className="feature-item feature-animate-1">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faCheck} />
                </div>
                <div className="feature-text">
                  <h3>Stručnost i iskustvo</h3>
                  <p>
                    Naš tim stručnjaka posjeduje višegodišnje iskustvo i
                    kontinuirano se educira o najnovijim tehnikama i trendovima.
                  </p>
                </div>
              </div>
              <div className="feature-item feature-animate-2">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faCheck} />
                </div>
                <div className="feature-text">
                  <h3>Personalizirani pristup</h3>
                  <p>
                    Svaki tretman prilagođavamo vašim željama i potrebama,
                    uzimajući u obzir oblik lica, boju kože i osobni stil.
                  </p>
                </div>
              </div>
              <div className="feature-item feature-animate-3">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faCheck} />
                </div>
                <div className="feature-text">
                  <h3>Premium proizvodi</h3>
                  <p>
                    Koristimo isključivo vrhunske proizvode koji su nježni prema
                    vašoj koži i osiguravaju dugotrajne rezultate.
                  </p>
                </div>
              </div>
            </div>
            <div className="about-quote">
              <blockquote>
                "Naša misija je istaknuti vašu prirodnu ljepotu kroz savršeno
                oblikovane obrve i trepavice koje će transformirati vaš izgled i
                samopouzdanje."
              </blockquote>
              <cite>- Tim Beauty Concept Veronika</cite>
            </div>
          </div>
        </div>
      </section>

      {/* Usluge sekcija - poboljšani prikaz */}
      <section
        id="services"
        className={`services-section ${
          isIntersecting.services ? "section-animate" : ""
        }`}
      >
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">Naše usluge</span>
            <h2 className="section-title">Specijalizirani tretmani</h2>
            <p className="section-description">
              Otkrijte naš asortiman profesionalnih tretmana obrva i trepavica
              prilagođenih vašim željama i potrebama.
            </p>
          </div>

          {loading ? (
            <div className="services-loading">
              <div className="spinner"></div>
              <p>Učitavanje tretmana...</p>
            </div>
          ) : (
            <div className="services-grid">
              {services.map((service, index) => (
                <div
                  key={service.id}
                  className={`service-card card-animate-${index % 4} ${
                    service.isOnSale ? "service-card-sale" : ""
                  }`}
                >
                  {service.isOnSale && (
                    <div className="sale-badge">
                      <span>{service.discountPercentage}% popusta</span>
                    </div>
                  )}
                  <div className="service-content">
                    <h3 className="service-title">{service.name}</h3>
                    <div className="service-meta">
                      <div className="service-price">
                        {service.isOnSale ? (
                          <>
                            <span className="old-price">{service.price} €</span>
                            <span className="sale-price">
                              {service.salePrice} €
                            </span>
                          </>
                        ) : (
                          <span>{service.price} €</span>
                        )}
                      </div>
                      {service.duration && (
                        <div className="service-duration">
                          <FontAwesomeIcon icon={faClock} />
                          <span>{service.duration} min</span>
                        </div>
                      )}
                    </div>
                    {service.description && (
                      <p className="service-description">
                        {service.description.length > 80
                          ? `${service.description.substring(0, 80)}...`
                          : service.description}
                      </p>
                    )}
                    <Link
                      to={`/services#${service.id}`}
                      className="service-link"
                    >
                      Saznaj više <FontAwesomeIcon icon={faArrowRight} />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="services-cta">
            <Link to="/services" className="primary-button">
              Pogledaj sve tretmane
            </Link>
          </div>
        </div>
      </section>

      {/* Dodano: Osvrti klijenata */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">Iskustva klijenata</span>
            <h2 className="section-title">Što kažu naši klijenti</h2>
          </div>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-rating">
                {[...Array(5)].map((_, i) => (
                  <FontAwesomeIcon key={i} icon={faStar} />
                ))}
              </div>
              <p className="testimonial-text">
                "Bila sam oduševljena rezultatima! Moje obrve sada izgledaju
                prirodno i savršeno uokviruju moje lice. Definitivno se vraćam!"
              </p>
              <div className="testimonial-author">
                <div className="author-info">
                  <h4>Ana K.</h4>
                  <span>Stalna klijentica</span>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-rating">
                {[...Array(5)].map((_, i) => (
                  <FontAwesomeIcon key={i} icon={faStar} />
                ))}
              </div>
              <p className="testimonial-text">
                "Profesionalan pristup i vrhunski rezultati. Moje trepavice
                nikad nisu izgledale bolje. Svakako preporučujem Beauty Concept
                Veronika!"
              </p>
              <div className="testimonial-author">
                <div className="author-info">
                  <h4>Martina P.</h4>
                  <span>Nova klijentica</span>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-rating">
                {[...Array(5)].map((_, i) => (
                  <FontAwesomeIcon key={i} icon={faStar} />
                ))}
              </div>
              <p className="testimonial-text">
                "Ugodan ambijent, stručno osoblje i kvalitetni proizvodi. Uvijek
                izađem zadovoljna nakon tretmana. Topla preporuka!"
              </p>
              <div className="testimonial-author">
                <div className="author-info">
                  <h4>Ivana M.</h4>
                  <span>Stalna klijentica</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA sekcija - s poboljšanim efektima */}
      <section
        id="cta"
        className={`cta-section ${isIntersecting.cta ? "section-animate" : ""}`}
      >
        <div className="container">
          <div className="home-cta-content">
            <h2 className="home-cta-title">
              Spremni za savršene obrve i trepavice?
            </h2>
            <p className="home-cta-description">
              Rezervirajte svoj termin već danas i dopustite nam da istaknemo
              vašu prirodnu ljepotu.
            </p>
            <div className="cta-buttons">
              <Link to="/booking" className="cta-button">
                <FontAwesomeIcon icon={faCalendarAlt} className="button-icon" />
                Rezerviraj svoj termin
              </Link>
              <Link to="/contact" className="cta-secondary-button">
                <FontAwesomeIcon icon={faEnvelope} className="button-icon" />
                Kontaktirajte nas
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
